import type { MechanicDefinition } from "./types";

export const playerInteractions: MechanicDefinition = {
  id: "player_interactions",
  disabled: true,
  name: "Player Interactions",
  description: "Enables trading, gifting, challenges, and alliances between players in multiplayer adventures.",
  icon: "Handshake",
  category: "systems",
  auditPasses: 1,

  configSchema: [
    {
      key: "enable_trading",
      label: "Enable Trading",
      type: "boolean",
      default: true,
      description: "Allow players to propose item trades (barter or currency-for-item) with each other.",
    },
    {
      key: "enable_gifts",
      label: "Enable Gifts",
      type: "boolean",
      default: true,
      description: "Allow one-way item gifts between players.",
    },
    {
      key: "enable_challenges",
      label: "Enable Challenges",
      type: "boolean",
      default: false,
      description: "Allow PvP duels and challenges between players.",
    },
    {
      key: "enable_alliances",
      label: "Enable Alliances",
      type: "boolean",
      default: false,
      description: "Allow formal alliance pacts between players.",
    },
    {
      key: "require_proximity",
      label: "Require Proximity",
      type: "boolean",
      default: false,
      description: "Players must be in the same location to interact.",
    },
  ],

  systemPromptSection: (config) => {
    const parts: string[] = [];
    parts.push("## Player Interactions");
    parts.push("Players can interact with each other outside of the main narrative flow.");

    if (config.enable_trading) {
      parts.push("- **Trading**: Players can propose and accept item trades (barter or currency-items-for-items). When a trade is accepted, use manage_inventory to move items between players' inventories. Currencies are inventory items.");
    }
    if (config.enable_gifts) {
      parts.push("- **Gifts**: Players can send one-way gifts of items. Use manage_inventory to transfer. Narrate received gifts naturally.");
    }
    if (config.enable_challenges) {
      parts.push("- **Challenges**: Players can challenge each other to duels. Resolve challenges using appropriate dice mechanics and narrate the outcome.");
    }
    if (config.enable_alliances) {
      parts.push("- **Alliances**: Players can form formal alliance pacts. Track alliance status and reference it in diplomatic situations.");
    }
    if (config.require_proximity) {
      parts.push("- Players must be in the same location to initiate interactions.");
    }
    parts.push("- When a player interaction is completed (accepted trade, received gift, etc.), weave it into the narrative naturally.");
    return parts.join("\n");
  },

  tools: [
    {
      type: "function",
      function: {
        name: "resolve_interaction",
        description: "Resolve a player interaction (trade, gift, challenge, alliance) that was accepted. Updates inventories and state accordingly.",
        parameters: {
          type: "object",
          properties: {
            interaction_type: {
              type: "string",
              enum: ["trade", "gift", "challenge", "alliance"],
              description: "Type of interaction to resolve",
            },
            from_player: { type: "string", description: "Name of the initiating player" },
            to_player: { type: "string", description: "Name of the receiving player" },
            items_from: {
              type: "array",
              items: { type: "object", properties: { name: { type: "string" }, quantity: { type: "number" } }, required: ["name"] },
              description: "Items given by the initiating player",
            },
            items_to: {
              type: "array",
              items: { type: "object", properties: { name: { type: "string" }, quantity: { type: "number" } }, required: ["name"] },
              description: "Items given by the receiving player (in trade)",
            },
            outcome: { type: "string", description: "Narrative outcome of the interaction" },
          },
          required: ["interaction_type", "from_player", "to_player"],
        },
      },
    },
  ],

  widgets: [
    {
      id: "player_interactions_widget",
      type: "sidebar_tab",
      label: "Interactions",
      icon: "Handshake",
      component: "PlayerInteractionsWidget",
      dataBindings: {},
      visibility: "when_active",
      priority: 80,
    },
  ],

  aiHints: {
    purpose: "Enables structured player-to-player interactions like trading, gifting, and challenges in multiplayer games.",
    whenToUse: "Enable for any multiplayer adventure where players should be able to interact with each other beyond just chatting.",
    synergies: ["economy", "inventory", "combat"],
    conflicts: [],
    prerequisites: [],
    tags: ["multiplayer", "social", "trading", "pvp"],
    exampleConfig: {
      enable_trading: true,
      enable_gifts: true,
      enable_challenges: false,
      enable_alliances: false,
      require_proximity: false,
    },
    stateVariables: ["pending_interactions", "completed_interactions"],
    complexity: 2,
  },

  initializeState: (config) => ({
    pending_interactions: [],
    completed_interactions: [],
  }),
};
